/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import {findIndex, maxBy} from 'lodash';
import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle
} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import {APP_COLORS} from '../../../common/Constants';
import './Geography.scss';

import {getSupplierRecordBySourceId} from '../../../common/services/SupplierService';
import {TYPES as SourceTypes} from '../../../admin/sources/SourceTypes';
import html2canvas from 'html2canvas'; // fallback only

mapboxgl.accessToken =
    'pk.eyJ1Ijoib3BzLWF0LWdldHRvb2xiZWx0LWRvdC1jb20iLCJhIjoiY2tqYWVlazdqN2FoajJ2cGRyaDM0b2w5dSJ9.0ghFxBToWVY2tiKhPDrs6w';

const initialCoordinates = [-96.5, 38.5];
const initialZoom = 2.5;

const USPS_TO_FIPS = {
    AL:'01', AK:'02', AZ:'04', AR:'05', CA:'06', CO:'08', CT:'09', DE:'10', FL:'12',
    GA:'13', HI:'15', ID:'16', IL:'17', IN:'18', IA:'19', KS:'20', KY:'21', LA:'22',
    ME:'23', MD:'24', MA:'25', MI:'26', MN:'27', MS:'28', MO:'29', MT:'30', NE:'31',
    NV:'32', NH:'33', NJ:'34', NM:'35', NY:'36', NC:'37', ND:'38', OH:'39', OK:'40',
    OR:'41', PA:'42', RI:'44', SC:'45', SD:'46', TN:'47', TX:'48', UT:'49', VT:'50',
    VA:'51', WA:'53', WV:'54', WI:'55', WY:'56', DC:'11', PR:'72'
};

const buildCountyFilter = (pairs) => ([
    'any',
    ...pairs
        .filter(t => t?.name && t?.state && USPS_TO_FIPS[t.state.toUpperCase()])
        .map(t => ([
            'all',
            ['==', ['get', 'NAME'], String(t.name).trim()],
            ['==', ['slice', ['get', 'GEOID'], 0, 2], USPS_TO_FIPS[t.state.toUpperCase()]]
        ]))
]);

const GeographyPanel = forwardRef(function GeographyPanel(props, ref) {
    const dispatch = useDispatch();

    // Loading Spinner
    const [loading, setLoading] = useState(true);

    // Map position
    const [lng, setLng] = useState(initialCoordinates[0]);
    const [lat, setLat] = useState(initialCoordinates[1]);
    const [zoom, setZoom] = useState(initialZoom);

    // Overlays / UI state
    const [renderedOverlays, setRenderedOverlays] = useState([]);
    const [renderedProducts, setRenderedProducts] = useState([]);
    const [showFlyout, setShowFlyout] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(null);

    // Placeholder for your product data; guard against undefined when building layers
    const mapData = undefined;

    // Map instance + container
    const [map, setMap] = useState();
    const mapContainer = useRef(null);

    // Cursor helpers
    const handleDataPointMouseEnter = (e) => {
        e.target.getCanvas().style.cursor = 'pointer';
    };
    const handleDataPointMouseLeave = (e) => {
        e.target.getCanvas().style.cursor = '';
    };

    // Initialize Mapbox map (mount once)
    const targetCounties = (props?.targetCounties ?? []).map(c => ({
        name: String(c?.county ?? '').trim(),
        state: String(c?.state ?? '').trim().toUpperCase()
    }));

    useEffect(() => {
        if (!map) return;
        const f = buildCountyFilter(targetCounties);
        if (map.getLayer('counties-name-state-highlight')) {
            map.setFilter('counties-name-state-highlight', f);
        }
        if (map.getLayer('counties-name-state-outline')) {
            map.setFilter('counties-name-state-outline', f);
        }
    }, [map, targetCounties]);

    useEffect(() => {
        const mapInstance = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/light-v10',
            center: [lng, lat],
            zoom: zoom,
            preserveDrawingBuffer: true
        });

        mapInstance.on('load', async () => {
            setMap(mapInstance);

            // --- Counties source
            const COUNTIES_URL =
                'https://raw.githubusercontent.com/loganpowell/census-geojson/master/GeoJSON/500k/2018/county.json';

            const resp = await fetch(COUNTIES_URL);
            const counties = await resp.json();

            if (!mapInstance.getSource('us-counties')) {
                mapInstance.addSource('us-counties', {
                    type: 'geojson',
                    data: counties,
                    generateId: true
                });
            }

            // Base fill (subtle)
            if (!mapInstance.getLayer('county-fill')) {
                mapInstance.addLayer({
                    id: 'county-fill',
                    type: 'fill',
                    source: 'us-counties',
                    paint: { 'fill-color': '#1f4963', 'fill-opacity': 0.05 }
                });
            }

            // Highlight layers with an initial filter (can be empty)
            const initialFilter = buildCountyFilter(targetCounties);

            if (!mapInstance.getLayer('counties-name-state-highlight')) {
                mapInstance.addLayer({
                    id: 'counties-name-state-highlight',
                    type: 'fill',
                    source: 'us-counties',
                    filter: initialFilter,
                    paint: { 'fill-color': '#ee5a38', 'fill-opacity': 0.35 }
                });
            }

            if (!mapInstance.getLayer('counties-name-state-outline')) {
                mapInstance.addLayer({
                    id: 'counties-name-state-outline',
                    type: 'line',
                    source: 'us-counties',
                    filter: initialFilter,
                    paint: { 'line-color': '#ee5a38', 'line-width': 2 }
                });
            }
        });

        return () => { try { mapInstance.remove(); } catch {} };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helper: wait for the map to be fully rendered
    const waitForMapIdle = () =>
        new Promise((resolve) => {
            if (!map) return resolve();
            if (map.loaded() && map.isStyleLoaded()) {
                map.once('idle', resolve);
                map.triggerRepaint();
            } else {
                map.once('load', () => map.once('idle', resolve));
            }
        });

    // Expose: get a PNG dataURL of the current map (used by PDF export)
    useImperativeHandle(ref, () => ({
        getMapImageData: async () => {
            if (!map) return null;
            try {
                await waitForMapIdle();
                return map.getCanvas().toDataURL('image/png');
            } catch {
                // Fallback to DOM rasterization if WebGL canvas is tainted
                try {
                    const el = map.getContainer();
                    const cv = await html2canvas(el, {useCORS: true});
                    return cv.toDataURL('image/png');
                } catch {
                    return null;
                }
            }
        }
    }));

    return (
        <div className="mapViewport">
            <div
                ref={mapContainer}
                className="insightMap"
                id="mapContainerID"
            />
        </div>
    );
});

export default GeographyPanel;
